/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import styled from 'styled-components';
import '@okta/okta-signin-widget/css/okta-sign-in.min.css';

// Styling to match https://www.figma.com/file/SjNobR41qV3KJkYJtfL4M1/GS-UP-OKTA-Login-Flow?type=design&node-id=3016%3A24716&mode=dev
const OktaFormWrapper = styled.div`
  display: grid;
  justify-content: center;
  justify-items: center;
  margin-top: 56px;
  position: relative;
  z-index: 10;

  * {
    font-family: 'Source Sans Pro', sans-serif !important;
  }

  .login-spinner {
    width: 40px;
    height: 40px;
    position: absolute;
    top: 160px;
    color: white;
    z-index: 9;
  }

  .viasat-logo {
    max-width: 180px;
    align-items: center;
    margin-bottom: 32px;
  }

  #okta-sign-in {
    margin: 0 auto 0;
    width: 542px;
    overflow: hidden;
    z-index: 10;
  }

  #okta-sign-in .auth-content {
    padding: 0 42px;
    padding-top: 0px !important;
  }

  #okta-sign-in.auth-container.main-container {
    background: rgba(0, 0, 0, 0);
    border: 0;
    color: #fff;
    box-shadow: none;
  }

  #okta-sign-in.auth-container .okta-sign-in-header {
    background: rgba(0, 0, 0, 0);
    border: 0;
    padding: 0 90px;
  }

  /* Disable the giant/ugly images Okta shows when signing in. */
  #okta-sign-in .beacon-container {
    display: none;
  }

  #okta-sign-in .siw-main-body {
    padding: 32px 48px;

    border-radius: 16px;
    background: #202e39;
  }

  #okta-sign-in .okta-form-title {
    font-size: 24px;
    font-style: normal;
    font-weight: 800;
    line-height: 32px;
    letter-spacing: 0.2px;
  }

  #okta-sign-in .o-form-button-bar {
    padding: 24px 0 0;
  }

  #okta-sign-in .o-form-head {
    margin-top: 0;
  }

  #okta-sign-in .o-form-content {
    padding-bottom: 0;
  }

  #okta-sign-in .siw-main-footer {
    display: flex;
    align-items: center;
    flex-direction: column;
  }

  #okta-sign-in .siw-main-footer a {
    align-self: center;
    font-size: 14px;
  }

  #okta-sign-in .siw-main-footer a:first-child {
    margin-top: 16px;
  }

  #okta-sign-in.auth-container .okta-form-label {
    color: #9fafbc;
  }

  /* Viasat Beam Button */
  #okta-sign-in.auth-container .button-primary {
    color: #202e39;
    background-image: none !important;
    border: 0;

    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: 24px;

    height: 48px;
    padding: 8px 28px;

    border-radius: 1000px;
    background: #18ecfc;
  }

  #okta-sign-in.auth-container .button-primary:hover {
    background: #c6fcff;
  }

  #okta-sign-in.auth-container .button-primary:active,
  #okta-sign-in.auth-container .button-primary:focus {
    background-color: #fff;
    box-shadow: none;
    border: 0;
  }

  #okta-sign-in.auth-container .button-primary:disabled,
  #okta-sign-in.auth-container .button.button-primary.link-button-disabled {
    color: #465967;
    background-color: #677a89;
  }

  #okta-sign-in .infobox-error {
    padding-left: 44px !important;
    padding-right: 16px !important;

    border-radius: 8px;
    border: 0;

    background: rgba(250, 114, 114, 0.1) !important;
    white-space: pre-line; /* Added to allow line breaks in the error messages */
  }

  #okta-sign-in .infobox-error:before {
    border-radius: 8px 0 0 8px;
    color: #fa7272;
    background: transparent;
  }

  #okta-sign-in .infobox-error .error-16:before {
    padding-left: 5px;
    color: rgba(250, 114, 114, 1);
    font-size: 18px;
  }

  #okta-sign-in .infobox-warning {
    padding-left: 44px !important;
    padding-right: 16px !important;

    border-radius: 8px;
    border: 0;

    background: rgba(253, 195, 97, 0.1) !important;
  }

  #okta-sign-in .infobox-warning:before {
    border-radius: 8px 0 0 8px;
    color: #fa7272;
    background: transparent;
  }

  #okta-sign-in .infobox-warning .warning-16:before {
    padding-left: 5px;
    color: rgba(253, 195, 97, 1);
    font-size: 18px;
  }

  #okta-sign-in .o-form-fieldset-container {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  #okta-sign-in .o-form-fieldset {
    margin: 0;
  }

  #okta-sign-in .o-form .o-form-fieldset:last-child {
    margin-bottom: 0;
  }

  /* Viasat Beam Checkbox; svg's are from their component Figma guide. */
  #okta-sign-in .o-form .custom-checkbox label {
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;

    font-size: 16px;
    padding: 0;
    background-image: none !important;
  }

  #okta-sign-in .o-form .custom-checkbox label::before {
    width: 20px;
    height: 20px;
    display: inline-block;
    align-self: center;
    content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'%3E%3Crect x='0.5' y='0.5' width='19' height='19' rx='1.5' stroke='%23C3CDD5'/%3E%3C/svg%3E");
  }

  #okta-sign-in .o-form .custom-checkbox:hover label::before {
    position: relative;
    left: -6px;
    top: -6px;
    content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32' fill='none'%3E%3Crect x='3' y='3' width='26' height='26' rx='5' stroke='%23111920' stroke-width='6'/%3E%3Crect x='7' y='7' width='18' height='18' rx='1' stroke='%2300A2C0' stroke-width='2'/%3E%3C/svg%3E");
  }

  #okta-sign-in .o-form .custom-checkbox:hover label.checked::before {
    position: relative;
    left: -6px;
    top: -6px;
    content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='32' height='32' viewBox='0 0 32 32' fill='none'%3E%3Crect x='3' y='3' width='26' height='26' rx='5' stroke='%23111920' stroke-width='6'/%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M8 6C6.89543 6 6 6.89543 6 8V24C6 25.1046 6.89543 26 8 26H24C25.1046 26 26 25.1046 26 24V8C26 6.89543 25.1046 6 24 6H8ZM10.1302 15.736L13.4491 19.4676L21.8778 10L23 11.2617L13.4491 22L9 16.9978L10.1302 15.736Z' fill='%2300A2C0'/%3E%3C/svg%3E");
  }

  #okta-sign-in .o-form .custom-checkbox label.checked::before {
    content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M2 0C0.895431 0 0 0.895431 0 2V18C0 19.1046 0.895431 20 2 20H18C19.1046 20 20 19.1046 20 18V2C20 0.895431 19.1046 0 18 0H2ZM4.13019 9.73599L7.44912 13.4675L15.8778 3.99997L17 5.26171L7.44912 16L3 10.9977L4.13019 9.73599Z' fill='%2300C2DE'/%3E%3C/svg%3E");
  }

  #okta-sign-in.auth-container h2,
  #okta-sign-in.auth-container h3 {
    color: #fff;
  }

  a,
  a:visited,
  a:link,
  .button-link,
  .button-link:visited,
  .button-link:link {
    color: #009de0 !important;
    padding: 0;
    text-decoration-line: none;
  }

  a:active,
  .button-link:active {
    color: #fff;
  }

  a:hover,
  .button-link:hover {
    color: #14b9ff;
    text-decoration-line: underline;
  }

  a:focus,
  .button-link:focus {
    color: #14b9ff;
    border-radius: 4px;
    border: 1px dashed #fff;
  }

  #okta-sign-in .siw-main-footer .auth-footer {
    gap: 8px;
  }

  .infobox,
  .infobox-md {
    border: 1px solid #ad0000;
    border-radius: 3px;
    background-color: #fbfbfb;
  }

  .ajax-form-editor .infobox,
  .edit-form .infobox,
  .form-content-wrap .infobox,
  .m-form .infobox,
  .o-form .infobox,
  .read-only-form .infobox,
  .v-form .infobox {
    background-color: #4d2626;
  }

  #okta-sign-in.auth-container .okta-form-input-field input,
  #okta-sign-in.auth-container .okta-form-input-field input:autofill,
  #okta-sign-in.auth-container .okta-form-input-field input:-webkit-autofill {
    padding: 10px 16px !important;
    box-shadow: 0 0 0 1000px #1c262f inset;
    color: #fff;
    -webkit-text-fill-color: #fff;
  }

  #okta-sign-in.auth-container .okta-form-input-field {
    border-radius: 3px;
    background-color: transparent;
  }

  .okta-form-label:has(+ .o-form-has-errors),
  #okta-sign-in .o-form-explain.o-form-input-error,
  #okta-sign-in .error-16-red:before,
  #okta-sign-in .error-16-small:before {
    color: #fa7272 !important;
  }

  #okta-sign-in.auth-container .okta-form-input-field.o-form-has-errors {
    border: 1px solid #f06c6c !important;
  }

  #okta-sign-in.auth-container .okta-form-input-field:hover {
    border: 1px solid #9fafbc;
  }

  #okta-sign-in.auth-container .okta-form-input-field:hover {
    border: 1px solid #c3cdd5;
  }

  #okta-sign-in.auth-container .okta-form-input-field:focus {
    border: 1px solid #18ecfc;
  }

  #okta-sign-in.auth-container .okta-form-input-field:hover {
    border-color: #1684c9;
  }

  #okta-sign-in.auth-container .okta-form-subtitle {
    font-size: 16px;
    color: #fff;
  }

  #okta-sign-in .siw-main-view .siw-main-body span.strong {
    color: #fff;
  }

  #okta-sign-in span.strong.no-translate {
    all: revert;
  }

  #okta-sign-in .password-authenticator--list {
    margin-bottom: 0;
  }

  #okta-sign-in .siw-main-view .siw-main-body .identifier-container {
    margin-bottom: 11px;
    margin-top: 26px;
    color: rgba(159, 175, 188, 1);
  }

  #okta-sign-in .enter-auth-code-instead-link {
    margin: 0 auto;
    text-align: center;
    padding-bottom: 0;
  }

  #okta-sign-in .otp-value {
    color: #9fafbc;
    font-size: 44px;
  }

  #okta-sign-in .otp-warning {
    color: #9fafbc;
  }

  #okta-sign-in .authenticator-row {
    justify-content: center;
  }

  #okta-sign-in .js-help {
    display: none;
  }

  #okta-sign-in .authenticator-row .authenticator-button .button {
    color: #202e39 !important;
    background: #18ecfc;
    border: 0;
    border-radius: 1000px;
    font-weight: 600;
    font-size: 16px;
  }

  #okta-sign-in .authenticator-row .authenticator-button .button:hover {
    background: #c6fcff;
  }

  #okta-sign-in .authenticator-verify-list .authenticator-description .authenticator-label {
    font-size: 16px;
    width: 150px;
  }

  #okta-sign-in .authenticator-row .authenticator-icon-container {
    align-self: flex-start;
    margin-right: 10px;
  }

  .piv-verify-text {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  #okta-sign-in .okta-waiting-spinner {
    background-image: none !important;
    width: 22px;
    height: 22px;
    position: relative;
    margin-top: 10px;
  }

  #okta-sign-in .okta-waiting-spinner::before {
    content: '';
    position: absolute;
    inset: 0;
    box-sizing: border-box;
    border-radius: 50%;
    border: 2px solid #fff;
    border-right-color: transparent;
    animation: spin 1.4s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  #okta-sign-in.auth-container .skip-button {
    color: #ffffff;
    border: none;

    width: fit-content;
    font-size: 18px;
    font-weight: 600;
    line-height: 24px;

    margin-top: 16px;
    margin-left: auto !important;
    margin-right: auto;
    height: 48px;
    padding: 8px 28px;

    background: transparent;
    box-shadow: none;
  }

  #okta-sign-in.auth-container .skip-button:hover {
    background: transparent;
  }

  .help-link {
    margin: 0;
    color: #fff;
    font-size: 14px;
    margin-top: 16px;
  }

  .mil-id-info-text {
    font-size: 16px !important;
    line-height: 24px !important;
    padding-bottom: 16px !important;
  }

  /* This rule has to be at the bottom. I'm not sure why. If you move it above, all rules that follow it won't be applied. */
  #okta-sign-in .siw-main-view .siw-main-body .identifier-container:before {
    content: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'%3E%3Cg clip-path='url(%23clip0_3050_2551)'%3E%3Cpath d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM7.07 18.28C7.5 17.38 10.12 16.5 12 16.5C13.88 16.5 16.51 17.38 16.93 18.28C15.57 19.36 13.86 20 12 20C10.14 20 8.43 19.36 7.07 18.28ZM18.36 16.83C16.93 15.09 13.46 14.5 12 14.5C10.54 14.5 7.07 15.09 5.64 16.83C4.62 15.49 4 13.82 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 13.82 19.38 15.49 18.36 16.83ZM12 6C10.06 6 8.5 7.56 8.5 9.5C8.5 11.44 10.06 13 12 13C13.94 13 15.5 11.44 15.5 9.5C15.5 7.56 13.94 6 12 6ZM12 11C11.17 11 10.5 10.33 10.5 9.5C10.5 8.67 11.17 8 12 8C12.83 8 13.5 8.67 13.5 9.5C13.5 10.33 12.83 11 12 11Z' fill='%239FAFBC'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_3050_2551'%3E%3Crect width='24' height='24' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
    vertical-align: -40%;
  }
`;

export default OktaFormWrapper;
